import { AnimatePresence, motion } from 'framer-motion';
import { Download, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOverlay } from '../../contexts/OverlayContext';
import profileImageCache from '../../services/profileImageCache';
import userService from '../../services/userService';
import { productionTemplateApi } from './templateApi';

const ProductTemplateDetailsModal = ({
  template,
  isOpen,
  onClose,
  onDeleteSuccess,
}) => {
  const navigate = useNavigate();
  const { openOverlay } = useOverlay();
  const [imageUrl, setImageUrl] = useState(null);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [imageError, setImageError] = useState(null);
  const [fullTemplate, setFullTemplate] = useState(template);

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.2 } },
  };

  // Fetch full template details if photo_key is missing
  useEffect(() => {
    const fetchFullTemplate = async () => {
      if (!template?.photo_key && template?.production_template_id) {
        console.log('🖼️ Product Template Modal: Fetching full template details for photo_key...');
        try {
          const result = await productionTemplateApi.getTemplate(template.production_template_id);
          if (result.success && result.template) {
            console.log('✅ Product Template Modal: Got full template details');
            setFullTemplate({ ...template, ...result.template });
          }
        } catch (error) {
          console.error('❌ Product Template Modal: Error fetching template details:', error);
        }
      } else {
        setFullTemplate(template);
      }
    };

    if (template) {
      fetchFullTemplate();
    }
  }, [template]);

  // Fetch image URL when fullTemplate changes
  useEffect(() => {
    const fetchImageUrl = async () => {
      if (!fullTemplate?.photo_key) {
        setImageUrl(null);
        setIsImageLoading(false);
        setImageError(null);
        return;
      }

      try {
        setIsImageLoading(true);
        setImageError(null);

        const photoKey = fullTemplate.photo_key;
        console.log('🖼️ Product Template Modal: Fetching image URL for photo_key:', photoKey);

        // Check if it's already a URL
        if (photoKey.startsWith('http://') || photoKey.startsWith('https://')) {
          console.log('🖼️ Product Template Modal: Using direct URL');
          setImageUrl(photoKey);
          setIsImageLoading(false);
          return;
        }

        // Normalize S3 path for consistent caching
        let s3Path = null;
        if (photoKey.startsWith('s3://')) {
          s3Path = photoKey;
        } else {
          s3Path = `s3://production-template-media/${photoKey}`;
        }

        console.log('🖼️ Product Template Modal: Normalized S3 path:', s3Path);

        // Check cache first
        const cachedUrl = profileImageCache.get(s3Path, true);
        if (cachedUrl) {
          console.log('✅ Product Template Modal: Using cached image URL');
          setImageUrl(cachedUrl);
          setIsImageLoading(false);
          return;
        }

        // Cache miss - fetch presigned URL (this will also cache it)
        console.log('🔄 Product Template Modal: Cache miss, fetching presigned URL...');
        const presignedUrl = await userService.getS3PresignedUrl(s3Path);
        
        if (presignedUrl) {
          console.log('✅ Product Template Modal: Got presigned URL (now cached)');
          setImageUrl(presignedUrl);
        } else {
          throw new Error('Failed to get presigned URL');
        }
      } catch (error) {
        console.error('❌ Product Template Modal: Error fetching image:', error);
        setImageError(error.message || 'Failed to load image');
        setImageUrl(null);
      } finally {
        setIsImageLoading(false);
      }
    };

    fetchImageUrl();
  }, [fullTemplate?.photo_key]);

  const handleDownload = (template) => {
    if (!template) return;

    const productDetails = {
      productName:
        template.template_name ||
        template.product_name ||
        template.productName ||
        '',
      category: template.category || '',
      sku: template.sku || '',
      type: template.type || '',
      price: template.price,
      cost: template.cost,
      unit: template.unit || '',
      barcode: template.barcode || '',
    };

    const perServeValue =
      template.per_serve != null ? Number(template.per_serve) : null;

    let totalCalories = 0;
    if (Array.isArray(template.bom) && template.bom.length > 0) {
      totalCalories = template.bom.reduce((sum, entry) => {
        const cal =
          entry && entry.calories != null ? Number(entry.calories) : NaN;
        return !Number.isNaN(cal) ? sum + cal : sum;
      }, 0);

      // Fallback to backend total_calories if BOM entries don't carry calories
      if (
        (!totalCalories || Number.isNaN(totalCalories)) &&
        template.total_calories != null
      ) {
        totalCalories = Number(template.total_calories) || 0;
      }
    } else if (template.total_calories != null) {
      totalCalories = Number(template.total_calories) || 0;
    }

    let caloriesPerServe = null;
    if (template.calories_per_serve != null) {
      caloriesPerServe = Number(template.calories_per_serve);
    } else if (perServeValue && perServeValue > 0 && totalCalories > 0) {
      caloriesPerServe = totalCalories / perServeValue;
    }

    const sopProcedures =
      template.sop?.procedures?.map((proc, idx) => ({
        id: proc.sop_no || `sop-${idx + 1}`,
        heading: proc.heading || '',
        points: proc.points || '',
      })) || [];

    const allergenItems =
      (Array.isArray(template.allergens) && template.allergens.length > 0
        ? template.allergens
        : (template.allergen_data?.list || []).map((item) =>
            typeof item === 'string' ? item : item.name
          )
      ).filter(Boolean);

    const allergenData = template.allergen_data || null;

    const bomEntries = Array.isArray(template.bom)
      ? template.bom.map((entry) => ({
          raw_material_id:
            entry.raw_material_id ||
            entry.production_template_id ||
            entry.item_id ||
            '',
          raw_material_name:
            entry.raw_material_name ||
            entry.raw_material ||
            entry.name,
          quantity: entry.quantity,
          unit: entry.unit,
          calories: entry.calories,
          specs: entry.specs,
        }))
      : [];

    const selectedSite =
      template.site_id || template.site_name
        ? {
            site_id: template.site_id,
            site_name: template.site_name,
            id: template.site_id,
            name: template.site_name,
          }
        : undefined;

    openOverlay('productTemplatePdfPreview', {
      imagePreview: imageUrl || null,
      productDetails,
      selectedSite,
      addToMenu: !!template.add_to_menu,
      productType: 'selling',
      bomEntries,
      stockItems: [],
      totalCalories,
      perServe:
        perServeValue != null && !Number.isNaN(perServeValue)
          ? String(perServeValue)
          : '',
      caloriesPerServe,
      sopProcedures,
      description: template.description || '',
      allergenItems,
      allergenData,
    });

    // Close the details modal once PDF preview/download is triggered
    if (onClose) {
      onClose();
    }
  };

  const handleDelete = () => {
    const templateId = fullTemplate?.production_template_id;
    if (!templateId) return;

    const templateName =
      fullTemplate?.template_name ||
      fullTemplate?.product_name ||
      fullTemplate?.productName ||
      'Unnamed';

    openOverlay('deleteProductTemplateWarning', {
      templateName,
      templateId,
      onDeleted: () => {
        if (typeof onDeleteSuccess === 'function') {
          onDeleteSuccess();
        }
        if (onClose) {
          onClose();
        }
      },
    });
  };

  if (!isOpen || !fullTemplate) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <h2 className="text-2xl font-bold text-gray-800">
                {fullTemplate.template_name || fullTemplate.product_name || fullTemplate.productName || 'Product'} - Details
              </h2>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-6 h-6 text-gray-600" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              {/* Product Image Section */}
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  Product Image
                </h3>
                {!fullTemplate.photo_key ? (
                  <div className="w-full p-8 bg-gray-50 rounded-xl border border-gray-200 flex flex-col justify-center items-center gap-2">
                    <p className="text-base font-medium text-gray-700">
                      No image uploaded
                    </p>
                    <p className="text-sm text-gray-500">This template does not have a product image</p>
                  </div>
                ) : isImageLoading ? (
                  <div className="w-full p-8 bg-gray-50 rounded-xl border border-gray-200 flex flex-col justify-center items-center gap-2">
                    <div className="text-gray-500">Loading image...</div>
                  </div>
                ) : imageError ? (
                  <div className="w-full p-8 bg-gray-50 rounded-xl border border-gray-200 flex flex-col justify-center items-center gap-2">
                    <p className="text-sm text-red-500 mb-2">{imageError}</p>
                    <p className="text-xs text-gray-400">Photo Key: {fullTemplate.photo_key}</p>
                  </div>
                ) : imageUrl ? (
                  <div className="flex flex-col items-center">
                    <img
                      src={imageUrl}
                      alt="Product"
                      className="max-w-full max-h-64 rounded-lg object-contain shadow-md"
                      onError={(e) => {
                        console.error('Image load error');
                        setImageError('Failed to load image');
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                ) : (
                  <div className="w-full p-8 bg-gray-50 rounded-xl border border-gray-200 flex flex-col justify-center items-center gap-2">
                    <p className="text-sm text-gray-500">Image not available</p>
                    <p className="text-xs text-gray-400 mt-1">Photo Key: {fullTemplate.photo_key}</p>
                  </div>
                )}
              </div>

              {/* Product Information Section */}
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  Product Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-semibold text-gray-600">Product Name</label>
                    <p className="text-lg font-medium text-gray-800 mt-1">
                      {fullTemplate.template_name || fullTemplate.product_name || fullTemplate.productName || 'N/A'}
                    </p>
                  </div>
                  {fullTemplate.category && (
                    <div>
                      <label className="text-sm font-semibold text-gray-600">Category</label>
                      <p className="text-lg font-medium text-gray-800 mt-1">
                        {fullTemplate.category}
                      </p>
                    </div>
                  )}
                  {fullTemplate.sku && (
                    <div>
                      <label className="text-sm font-semibold text-gray-600">SKU</label>
                      <p className="text-lg font-medium text-gray-800 mt-1">
                        {fullTemplate.sku}
                      </p>
                    </div>
                  )}
                  {fullTemplate.price !== undefined && (
                    <div>
                      <label className="text-sm font-semibold text-gray-600">Selling Price</label>
                      <p className="text-lg font-medium text-gray-800 mt-1">
                        ₹{fullTemplate.price}
                      </p>
                    </div>
                  )}
                  {fullTemplate.cost !== undefined && (
                    <div>
                      <label className="text-sm font-semibold text-gray-600">Cost Price</label>
                      <p className="text-lg font-medium text-gray-800 mt-1">
                        ₹{fullTemplate.cost}
                      </p>
                    </div>
                  )}
                  {fullTemplate.unit && (
                    <div>
                      <label className="text-sm font-semibold text-gray-600">Unit</label>
                      <p className="text-lg font-medium text-gray-800 mt-1">
                        {fullTemplate.unit}
                      </p>
                    </div>
                  )}
                  {fullTemplate.barcode && (
                    <div>
                      <label className="text-sm font-semibold text-gray-600">Barcode</label>
                      <p className="text-lg font-medium text-gray-800 mt-1">
                        {fullTemplate.barcode}
                      </p>
                    </div>
                  )}
                  {fullTemplate.description && (
                    <div className="col-span-1 md:col-span-2">
                      <label className="text-sm font-semibold text-gray-600">Description</label>
                      <p className="text-base text-gray-800 mt-1">
                        {fullTemplate.description}
                      </p>
                    </div>
                  )}
                  {fullTemplate.created_at && (
                    <div>
                      <label className="text-sm font-semibold text-gray-600">Created Date</label>
                      <p className="text-lg font-medium text-gray-800 mt-1">
                        {new Date(fullTemplate.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  )}
                  {fullTemplate.site_name && (
                    <div>
                      <label className="text-sm font-semibold text-gray-600">Site</label>
                      <p className="text-lg font-medium text-gray-800 mt-1">
                        {fullTemplate.site_name}
                      </p>
                    </div>
                  )}
                  {fullTemplate.add_to_menu != null && (
                    <div>
                      <label className="text-sm font-semibold text-gray-600">Add to menu</label>
                      <p className="text-lg font-medium text-gray-800 mt-1">
                        {fullTemplate.add_to_menu ? 'Yes' : 'No'}
                      </p>
                    </div>
                  )}
                  {fullTemplate.calories_per_serve != null && fullTemplate.calories_per_serve !== '' && (
                    <div>
                      <label className="text-sm font-semibold text-gray-600">Calories per serve</label>
                      <p className="text-lg font-medium text-gray-800 mt-1">
                        {Number(fullTemplate.calories_per_serve)}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* SOP (Standard Operating Procedure) */}
              {fullTemplate.sop?.procedures?.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">
                    Standard Operating Procedure
                  </h3>
                  <div className="space-y-4">
                    {fullTemplate.sop.procedures.map((proc, idx) => (
                      <div key={idx} className="border border-gray-200 rounded-xl p-4 bg-gray-50">
                        <h4 className="text-sm font-semibold text-gray-700 mb-2">
                          {proc.sop_no ? `${proc.sop_no}. ` : ''}{proc.heading || 'Procedure'}
                        </h4>
                        {(proc.points || '')
                          .split(/\r?\n/)
                          .map((line, i) => line.trim())
                          .filter(Boolean)
                          .length > 0 ? (
                          <ul className="list-disc list-inside text-sm text-gray-800 space-y-1">
                            {(proc.points || '')
                              .split(/\r?\n/)
                              .map((line, i) => line.trim())
                              .filter(Boolean)
                              .map((line, i) => (
                                <li key={i}>{line}</li>
                              ))}
                          </ul>
                        ) : (
                          <p className="text-sm text-gray-500">No steps</p>
                        )}
                      </div>
                    ))}
                    {fullTemplate.sop.estimated_calories != null && (
                      <p className="text-sm text-gray-600">
                        <span className="font-semibold">Estimated calories:</span>{' '}
                        {fullTemplate.sop.estimated_calories}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Allergens */}
              {(fullTemplate.allergens?.length > 0 || fullTemplate.allergen_data?.list?.length > 0) && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">
                    Allergens
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {(fullTemplate.allergens && fullTemplate.allergens.length > 0
                      ? fullTemplate.allergens
                      : (fullTemplate.allergen_data?.list || []).map((item) =>
                          typeof item === 'string' ? item : item.name
                        )
                    ).map((name, i) => (
                      <span
                        key={i}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-amber-100 text-amber-800"
                      >
                        {name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Dialog Actions */}
              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={() => handleDownload(fullTemplate)}
                  className="flex-1 flex items-center justify-center gap-2 bg-blue-700 text-white px-6 py-3 rounded-xl font-semibold hover:bg-blue-800 transition-colors"
                  style={{ color: '#ffffff' }}
                >
                  <Download
                    className="w-5 h-5 text-white stroke-white"
                    strokeWidth={2}
                  />
                  <span style={{ color: '#ffffff' }}>Download Details</span>
                </button>
                <button
                  onClick={() => {
                    if (fullTemplate) {
                      navigate('/onboarding-product-template/create', {
                        state: { template: fullTemplate, mode: 'edit' },
                      });
                    }
                    if (onClose) {
                      onClose();
                    }
                  }}
                  className="flex-1 bg-green-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-green-700 transition-colors"
                  style={{ color: '#ffffff' }}
                >
                  <span style={{ color: '#ffffff' }}>Edit</span>
                </button>
                <button
                  onClick={handleDelete}
                  disabled={!fullTemplate?.production_template_id}
                  className="flex-1 bg-red-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-red-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{ color: '#ffffff' }}
                >
                  <span style={{ color: '#ffffff' }}>Delete</span>
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ProductTemplateDetailsModal;

